import { useEffect, useState } from "react";
import { Button } from "@/components/common/Button";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { ChecklistRow, type RowState } from "../primitives/ChecklistRow";
import { TokenInput } from "../primitives/TokenInput";

/**
 * Fourth step — the app provisions itself.
 *
 * This replaced "paste your OpenRouter API key" (ADR-0014 §5, amended
 * 2026-07-29). The org pays for model access, the program database and the
 * MCP gateway; asking a counsellor to go create an OpenRouter account and buy
 * credits was homework they couldn't do, for a credential we already hold. So
 * the screen now *fetches* rather than asks: the cloud mints this user's key,
 * a desktop pulls the same set down over its authenticated cloud credential.
 *
 * Setup does NOT continue past this screen until provisioning succeeds (@dev):
 * an install with no model access can't answer anything, and one with no
 * gateway key can't see Nbyula data — both surface later as "the app is
 * broken", far from the screen that could have explained why. So the failure
 * state is actionable instead: what's missing, who can fix it (a role problem
 * needs engineering, not a retry), and a Recheck button that re-runs the whole
 * provisioning pass in place.
 */

/** Row icons — small, flat glyphs matching the environment-check step's set. */
function ModelIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8}>
      <path d="M12 3l2.2 5.6L20 10.8l-5.8 2.2L12 18.6l-2.2-5.6L4 10.8l5.8-2.2L12 3z" strokeLinejoin="round" />
    </svg>
  );
}

function DatabaseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8}>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6" strokeLinecap="round" />
      <path d="M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" strokeLinecap="round" />
    </svg>
  );
}

function GatewayIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8}>
      <rect x="3" y="4" width="18" height="7" rx="2" />
      <rect x="3" y="13" width="18" height="7" rx="2" />
      <path d="M7 7.5h.01M7 16.5h.01" strokeLinecap="round" />
    </svg>
  );
}

export function ProvisionStep() {
  const {
    provisionState,
    provision,
    token,
    tokenValidation,
    setToken,
    verifyToken,
    goNext,
  } = useOnboardingStore();
  const [showManual, setShowManual] = useState(false);

  // Fetch on arrival. Guarded on ``idle`` so a re-render (or coming back to
  // the step) doesn't re-mint.
  useEffect(() => {
    if (provisionState.kind === "idle") void provision();
  }, [provisionState.kind, provision]);

  // Auto-advance only when provisioning actually completed. The user cannot
  // walk past a half-provisioned install (@dev): without model access nothing
  // runs, and without a gateway key the agent has no Nbyula data — an app in
  // either state looks broken later, far from this screen where the cause is
  // still legible. A pasted BYOK key re-runs provisioning rather than
  // advancing, so the gateway half is still checked.
  useEffect(() => {
    if (provisionState.kind !== "done") return;
    const id = window.setTimeout(() => goNext(), 900);
    return () => window.clearTimeout(id);
  }, [provisionState.kind, goNext]);

  // A verified pasted key covers model access — re-check so the screen can
  // clear if that was the only thing missing.
  useEffect(() => {
    if (tokenValidation.kind === "verified") void provision();
  }, [tokenValidation.kind, provision]);

  const running = provisionState.kind === "running" || provisionState.kind === "idle";
  const status = provisionState.kind === "done" || provisionState.kind === "unavailable"
    ? provisionState.status
    : null;

  const rowState = (ok: boolean | undefined): RowState =>
    running ? "checking" : ok ? "ok" : "missing";

  return (
    <div className="w-full">
      <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.01em] text-space">
        {running
          ? "Setting up your workspace…"
          : provisionState.kind === "done"
            ? "You're all set up."
            : "Almost — one thing didn't come through"}
      </h1>
      <p className="mt-1.5 text-[14px] leading-relaxed text-space-50">
        {running
          ? "Fetching your model access and workspace credentials from OmniWerk. No keys to type — the org already has these."
          : provisionState.kind === "done"
            ? "Everything came through. Nothing for you to configure."
            : provisionState.message}
      </p>

      <div className="mt-6 space-y-2">
        <ChecklistRow
          icon={<ModelIcon />}
          label="Model access"
          state={rowState(status?.openrouter)}
          detail={
            running
              ? "Provisioning your key…"
              : status?.openrouter
                ? "Ready — Claude, GPT, Gemini and more."
                : "Not provisioned yet."
          }
        />
        <ChecklistRow
          icon={<DatabaseIcon />}
          label="Program database"
          state={rowState(status?.opensearch)}
          detail={
            running
              ? "Fetching credentials…"
              : status?.opensearch
                ? "Connected."
                : "An admin sets this up for the workspace."
          }
        />
        <ChecklistRow
          icon={<GatewayIcon />}
          label="Nbyula data gateway"
          state={rowState(status?.gateway)}
          detail={
            running
              ? "Provisioning your key…"
              : status?.gateway
                ? "Connected — your own key, your access level."
                : status?.gateway_reason === "role_not_allowed"
                  ? "Your Nbyula role doesn't permit data access."
                  : status?.gateway_reason === "no_account"
                    ? "No Nbyula account found for your email."
                    : "Not provisioned yet."
          }
        />
      </div>

      {provisionState.kind === "unavailable" && (
        <div className="mt-6">
          <div className="flex items-center justify-between gap-3">
            <Button variant="primary" size="sm" onClick={() => void provision()}>
              Recheck
            </Button>
            {/* Bring-your-own only helps the MODEL key — a gateway key can't be
                self-served, so offering it there would be a dead end. */}
            {!showManual && !status?.openrouter && (
              <button
                onClick={() => setShowManual(true)}
                className="text-[14px] font-medium text-jove hover:text-jove-200"
              >
                Use my own OpenRouter key →
              </button>
            )}
          </div>

          {showManual && (
            <div className="mt-5">
              <label className="mb-2 block text-[14px] font-medium text-space">
                Paste your OpenRouter API key
              </label>
              <TokenInput
                value={token}
                onChange={setToken}
                onSettled={verifyToken}
                validation={tokenValidation}
                locked={tokenValidation.kind === "verified"}
                placeholder="sk-or-..."
              />
              <p className="mt-2 text-[12px] font-medium leading-relaxed tracking-[0.04em] text-space-50">
                Only if you'd rather bill your own account —{" "}
                <a
                  href="https://openrouter.ai/keys"
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-jove hover:text-jove-200"
                >
                  openrouter.ai/keys
                </a>
                . Your own key covers model access; the Nbyula gateway key is
                still provisioned for you.
              </p>
            </div>
          )}

          <p className="mt-6 text-[12px] font-medium leading-relaxed tracking-[0.04em] text-space-50">
            Setup stays here until this clears — OmniWerk can't do its job
            without it.
          </p>
        </div>
      )}
    </div>
  );
}
